import React, { useState } from 'react';
import { TrendingUp, TrendingDown, LogOut, Send, Globe, Loader2, Pencil, Check, X, Siren, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '../../api/client';
import { notifyTrackingChanged } from '../../lib/useMyTracking';
import LivePrice from './LivePrice';

const fmtPrice = (v) => {
    if (v === null || v === undefined || isNaN(v)) return '—';
    if (v >= 1000) return Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 });
    if (v >= 1) return Number(v).toLocaleString('en-US', { maximumFractionDigits: 4 });
    return Number(v).toLocaleString('en-US', { maximumFractionDigits: 8 });
};
const usd = (v) => `${v >= 0 ? '+' : '-'}$${Math.abs(v).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
const pct = (v, d = 2) => `${v >= 0 ? '+' : ''}${Number(v).toFixed(d)}%`;

// Live leveraged metrics (tick price overrides the backend snapshot for open rows)
export const liveMetrics = (item, price) => {
    const lev = item.leverage || 1;
    const isOpen = item.status === 'open';
    let move = item.pnl_pct ?? 0;
    if (isOpen && price && item.entry) {
        const raw = ((price - item.entry) / item.entry) * 100;
        move = item.side === 'LONG' ? raw : -raw;
    }
    const roe = isOpen ? move * lev : item.roe_pct ?? move * lev;
    const liqPrice = item.liquidation_price;
    const warnLevel = !isOpen ? 0 : roe <= -50 ? 50 : roe <= -30 ? 30 : 0;
    return {
        lev,
        move,
        roe,
        pnlUsd: item.amount ? (item.amount * roe) / 100 : null,
        warnLevel,
        nearStop: warnLevel >= 50,
        liqPrice,
        liq: liqPrice && price ? (Math.abs(price - liqPrice) / price) * 100 : null,
    };
};

const Cell = ({ label, value, className = 'text-slate-100' }) => (
    <div className="min-w-0">
        <div className="text-slate-400 uppercase tracking-wider text-[9px]">{label}</div>
        <div className={`font-mono truncate ${className}`}>{value}</div>
    </div>
);

const EditForm = ({ item, onDone }) => {
    const [amount, setAmount] = useState(item.amount ?? '');
    const [lev, setLev] = useState(item.leverage ?? item.system_leverage ?? 1);
    const [busy, setBusy] = useState(false);
    const save = async (e) => {
        e.stopPropagation();
        setBusy(true);
        try {
            await apiClient.patch(`/tracking/${item.id}`, { amount: amount ? Number(amount) : null, leverage: lev ? Number(lev) : null });
            toast.success(`#${item.short_id} güncellendi`);
            notifyTrackingChanged();
            onDone();
        } catch (err) {
            toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || 'Güncellenemedi');
        } finally {
            setBusy(false);
        }
    };
    const input = 'h-7 bg-slate-900 border border-slate-600 rounded-sm px-2 text-[11px] font-mono text-slate-100 outline-none focus:border-amber-400';
    return (
        <div className="flex items-center gap-2 flex-wrap mb-2" onClick={(e) => e.stopPropagation()} role="group">
            <label className="text-[10px] text-slate-400">Teminat $
                <input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid={`my-tracking-edit-amount-${item.short_id}`} className={`${input} w-24 ml-1`} />
            </label>
            <label className="text-[10px] text-slate-400">Kaldıraç x
                <input type="number" min="1" max="125" value={lev} onChange={(e) => setLev(e.target.value)} data-testid={`my-tracking-edit-leverage-${item.short_id}`} className={`${input} w-16 ml-1`} />
            </label>
            <button onClick={save} disabled={busy} data-testid={`my-tracking-edit-save-${item.short_id}`} className="h-7 px-2 inline-flex items-center gap-1 rounded-sm bg-amber-400 text-slate-950 text-[10px] font-bold hover:bg-amber-300 disabled:opacity-60">
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Kaydet
            </button>
            <button onClick={(e) => { e.stopPropagation(); onDone(); }} data-testid={`my-tracking-edit-cancel-${item.short_id}`} className="h-7 w-7 inline-flex items-center justify-center rounded-sm text-slate-400 hover:text-slate-100 hover:bg-slate-700">
                <X className="h-3 w-3" />
            </button>
        </div>
    );
};

const RowHeader = ({ item, m, isOpen, onEdit, onClose, busy }) => {
    const isLong = item.side === 'LONG';
    return (
        <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <span className={`h-6 w-6 rounded-md flex items-center justify-center ${isLong ? 'bg-emerald-500/15 text-emerald-300' : 'bg-rose-500/15 text-rose-300'}`}>
                {isLong ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
            </span>
            <span className="font-mono text-sm font-semibold text-slate-100">{item.symbol.replace('USDT', '')}</span>
            <span className={`text-[10px] font-bold ${isLong ? 'text-emerald-300' : 'text-rose-300'}`}>{item.side} {m.lev}x</span>
            <span className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded-sm bg-slate-900 border border-slate-600 text-slate-300">#{item.short_id}</span>
            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider text-slate-400" title="Takibe alındığı yer">
                {item.source === 'web' ? <Globe className="h-3 w-3" /> : <Send className="h-3 w-3" />}
                {item.source === 'web' ? 'Web' : 'Telegram'}
            </span>
            <span data-testid={`my-tracking-status-${item.short_id}`} className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-slate-600 bg-slate-700 text-slate-100">
                {item.status_tr}
            </span>
            {m.warnLevel >= 50 && (
                <span data-testid={`my-tracking-near-stop-${item.short_id}`} title="Kaldıraçlı zarar teminatın %50'sine ulaştı. Telegram'a uyarı gönderildi." className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-rose-600 text-white animate-pulse">
                    <Siren className="h-3 w-3" /> STOPA YAKIN
                </span>
            )}
            {m.warnLevel === 30 && (
                <span data-testid={`my-tracking-warn-30-${item.short_id}`} title="Kaldıraçlı zarar teminatın %30'unu geçti. Telegram'a ilk uyarı gönderildi; %50'de ikinci uyarı gelir." className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-400 text-slate-950">
                    <AlertTriangle className="h-3 w-3" /> DİKKAT · %30
                </span>
            )}
            {isOpen && (
                <span className="ml-auto flex items-center gap-1.5">
                    <button onClick={onEdit} data-testid={`my-tracking-edit-${item.short_id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-100 bg-slate-700 hover:bg-slate-600 border border-slate-600 rounded-sm px-2 py-0.5 transition-colors">
                        <Pencil className="h-3 w-3" /> Teminat/Kaldıraç
                    </button>
                    <button onClick={onClose} disabled={busy} data-testid={`my-tracking-close-${item.short_id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-100 bg-slate-700 hover:bg-rose-500/30 border border-slate-600 hover:border-rose-400/50 rounded-sm px-2 py-0.5 transition-colors disabled:opacity-60">
                        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <LogOut className="h-3 w-3" />} Kapattım
                    </button>
                </span>
            )}
        </div>
    );
};

export default function TrackingRow({ item, onSelect, tick }) {
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(false);
    const isOpen = item.status === 'open';
    const price = isOpen ? tick?.price ?? item.current_price : item.current_price;
    const m = liveMetrics(item, price);
    const roeColor = m.roe > 0 ? 'text-emerald-400' : m.roe < 0 ? 'text-rose-400' : 'text-slate-300';

    const close = async (e) => {
        e.stopPropagation();
        setBusy(true);
        try {
            await apiClient.post(`/tracking/${item.id}/close`);
            toast.success(`#${item.short_id} takibi kapatıldı`);
            notifyTrackingChanged();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Kapatılamadı');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={() => onSelect?.(item.symbol)}
            onKeyDown={(e) => e.key === 'Enter' && onSelect?.(item.symbol)}
            data-testid={`my-tracking-row-${item.short_id}`}
            className={`px-4 py-3 cursor-pointer transition-colors hover:bg-slate-700/40 ${isOpen ? '' : 'opacity-80'} ${
                m.warnLevel >= 50 ? 'bg-rose-950/40 border-l-2 border-rose-500' : m.warnLevel === 30 ? 'bg-amber-950/30 border-l-2 border-amber-400' : ''
            }`}
        >
            <RowHeader item={item} m={m} isOpen={isOpen} busy={busy} onClose={close} onEdit={(e) => { e.stopPropagation(); setEditing(true); }} />
            {editing && <EditForm item={item} onDone={() => setEditing(false)} />}
            {m.warnLevel > 0 && (
                <div
                    className={`text-[11px] rounded-sm px-2 py-1 mb-1.5 border ${m.warnLevel >= 50 ? 'text-rose-100 bg-rose-500/15 border-rose-500/40' : 'text-amber-100 bg-amber-500/10 border-amber-500/40'}`}
                    data-testid={`my-tracking-near-stop-msg-${item.short_id}`}
                >
                    Kaldıraçlı zarar teminatın <b>%{Math.abs(m.roe).toFixed(0)}</b>'i.
                    {m.liq !== null && <> Likidasyona yaklaşık <b>%{m.liq.toFixed(2)}</b> fiyat hareketi kaldı.</>}{' '}
                    {m.warnLevel >= 50 ? "Stop'unu kontrol et, uzaklaştırma." : "Stop'un yerinde mi kontrol et; %50'de ikinci uyarı gelir."}
                </div>
            )}
            {item.advice_tr && (
                <div className="text-[11px] text-amber-100 bg-amber-500/10 border border-amber-500/20 rounded-sm px-2 py-1 mb-1.5" data-testid={`my-tracking-advice-${item.short_id}`}>
                    <span className="font-semibold">Sistem diyor:</span> {item.advice_tr}
                    {item.reason_tr && <span className="text-slate-300"> {item.reason_tr}</span>}
                </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-2 text-[10px]">
                <Cell label="Giriş" value={`$${fmtPrice(item.entry)}`} />
                <Cell label={isOpen ? 'Şu an' : 'Kapanış'} value={<LivePrice symbol={item.symbol} tick={isOpen ? tick : undefined} fallbackPrice={price} className="text-[11px]" />} />
                <Cell label="Teminat × Kaldıraç" value={item.amount ? `$${item.amount} × ${m.lev}x = $${(item.amount * m.lev).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : `${m.lev}x (teminat girilmedi)`} />
                <Cell label="K/Z $ (kaldıraçlı)" className={`font-semibold ${roeColor}`} value={<span data-testid={`my-tracking-pnl-usd-${item.short_id}`}>{m.pnlUsd !== null ? usd(m.pnlUsd) : '—'}</span>} />
                <Cell label="K/Z teminata göre" className={`font-semibold ${roeColor}`} value={<span data-testid={`my-tracking-roe-${item.short_id}`}>{pct(m.roe, 1)}</span>} />
                <Cell label="Fiyat hareketi" className={roeColor} value={<span data-testid={`my-tracking-pnl-${item.short_id}`}>{pct(m.move)}</span>} />
                <Cell label={item.tp1_hit ? 'TP1 ✓ / TP2' : 'TP1 / TP2'} className="text-emerald-300" value={`$${fmtPrice(item.tp1)} / $${fmtPrice(item.tp2)}`} />
                <Cell label={item.tp1_hit ? 'Stop (girişte)' : 'Stop'} className="text-amber-300" value={`$${fmtPrice(item.tp1_hit ? item.entry : item.stop_loss)}`} />
                <Cell
                    label="Likidasyon (tahmini)"
                    className="text-rose-300"
                    value={
                        <span data-testid={`my-tracking-liq-${item.short_id}`} title="Binance izole marjin formülü, bakım marjini %0.5">
                            {m.liqPrice ? `$${fmtPrice(m.liqPrice)}${m.liq !== null ? ` · %${m.liq.toFixed(1)}` : ''}` : '1x — yok'}
                        </span>
                    }
                />
            </div>
        </div>
    );
}
